import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { cn } from "@/lib/utils";
import { t as tShared, useTranslation } from "@repo/shared/i18n";
import { useState } from "react";

import { dropdownArrow } from "../../../designSystemIcons";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import { TacticFormation } from "./TacticFormation";

type Props = {
  formationLabel: string;
  formationId: string;
  buildUpStyle: string;
  defensiveApproach: string;
  lineHeight: number;
  editable?: boolean;
  formations?: Formations;
  onChangeBuildUpStyle?: (style: string) => void;
  onChangeDefensiveApproach?: (approach: string) => void;
  onChangeLineHeight?: (height: number) => void;
  onChangeFormation?: (formation: string) => void;
};

// Keyed by the EA value names the tactic data carries; the copy lives in tactics.json.
export const BUILD_UP_STYLE_DESC_KEYS = {
  Balanced: "preset.buildUpStyleDesc.balanced",
  Counter: "preset.buildUpStyleDesc.counter",
  "Short Passing": "preset.buildUpStyleDesc.shortPassing",
} as const;

export const DEFENSIVE_APPROACH_DESC_KEYS = {
  Deep: "preset.defensiveApproachDesc.deep",
  Balanced: "preset.defensiveApproachDesc.balanced",
  High: "preset.defensiveApproachDesc.high",
  Aggressive: "preset.defensiveApproachDesc.aggressive",
} as const;

// The EA value stays the API identity (ADR 0024); only the label is translated.
const BUILD_UP_STYLE_KEYS = {
  Balanced: "tactics:buildUpStyle.balanced",
  Counter: "tactics:buildUpStyle.counter",
  "Short Passing": "tactics:buildUpStyle.shortPassing",
} as const;

const DEFENSIVE_APPROACH_KEYS = {
  Deep: "tactics:defensiveApproach.deep",
  Balanced: "tactics:defensiveApproach.balanced",
  High: "tactics:defensiveApproach.high",
  Aggressive: "tactics:defensiveApproach.aggressive",
} as const;

export const buildUpStyleLabel = (value: string) => {
  const key = BUILD_UP_STYLE_KEYS[value as keyof typeof BUILD_UP_STYLE_KEYS];
  return key ? tShared(key) : value;
};

export const defensiveApproachLabel = (value: string) => {
  const key =
    DEFENSIVE_APPROACH_KEYS[value as keyof typeof DEFENSIVE_APPROACH_KEYS];
  return key ? tShared(key) : value;
};

export const TacticalPreset = ({
  formationLabel,
  formationId,
  buildUpStyle,
  defensiveApproach,
  lineHeight,
  editable,
  formations,
  onChangeBuildUpStyle,
  onChangeDefensiveApproach,
  onChangeLineHeight,
  onChangeFormation,
}: Props) => {
  const ref = useOutsideClick(() => {
    setOpenFormation(false);
    setOpenBuildUpStyle(false);
    setOpenDefensiveApproach(false);
  });
  const [open, setOpen] = useState(true);
  const [openDefensiveApproach, setOpenDefensiveApproach] = useState(false);
  const [openBuildUpStyle, setOpenBuildUpStyle] = useState(false);
  const [openFormation, setOpenFormation] = useState(false);
  const buildUpStyles = ["Balanced", "Counter", "Short Passing"] as const;
  const defApproaches = ["Deep", "Balanced", "High", "Aggressive"] as const;
  const [showMore, setShowMore] = useState(!editable);
  const { t } = useTranslation(["tactics", "common"]);
  const buildUpStyleDescKey =
    BUILD_UP_STYLE_DESC_KEYS[
      buildUpStyle as keyof typeof BUILD_UP_STYLE_DESC_KEYS
    ];
  const defensiveApproachDescKey =
    DEFENSIVE_APPROACH_DESC_KEYS[
      defensiveApproach as keyof typeof DEFENSIVE_APPROACH_DESC_KEYS
    ];

  return (
    <div className="flex flex-col w-full">
      <button
        className={cn(
          "flex justify-between w-full h-6 px-2 bg-white/10 desktop:hover:bg-white/20",
          open
            ? "rounded-t-lg mobile:rounded-t-none sb-v2-lg:rounded-t-lg"
            : "rounded-lg mobile:rounded-none sb-v2-lg:rounded-lg",
        )}
        onClick={() => setOpen(!open)}
      >
        <h3 className="my-auto text-xs text-secondary-dark">
          {t("preset.title")}
        </h3>
        <div
          className={cn("pb-1 my-auto stroke-white/50", open && "rotate-180")}
        >
          {dropdownArrow}
        </div>
      </button>
      {open && (
        <div className="flex flex-col bg-white/[0.01] px-4 pt-2 pb-4 text-xs gap-y-3 rounded-b-lg mobile:rounded-b-none sb-v2-lg:rounded-b-lg border border-white/10 shadow-soft-dark-bottom">
          <div className="flex flex-col gap-y-2">
            <div className="flex justify-between font-semibold">
              <p>{t("preset.formation")}</p>
              {editable ? (
                <div className="relative flex flex-col">
                  <button
                    className="flex px-1 rounded bg-gray-775 desktop:hover:bg-gray-700 gap-x-0.5"
                    onClick={(e) => {
                      setOpenFormation(!openFormation);
                      setOpenBuildUpStyle(false);
                      setOpenDefensiveApproach(false);
                      e.stopPropagation();
                    }}
                  >
                    <p className="font-medium">{formationLabel}</p>
                    <div
                      className={cn(
                        "pb-[2px] my-auto stroke-white",
                        openFormation && "rotate-180",
                      )}
                    >
                      {dropdownArrow}
                    </div>
                  </button>
                  {openFormation && (
                    <div className="absolute p-0.5 rounded bg-gray-775 top-[18px] z-20 gap-y-1 right-0 shadow-dropdown max-h-52 overflow-y-auto overflow-x-hidden">
                      {Object.entries(formations).map(([value, { label }]) => (
                        <button
                          key={value}
                          className="w-full text-xs font-medium text-right rounded-sm desktop:hover:bg-gray-700 whitespace-nowrap px-0.5"
                          onClick={() => {
                            onChangeFormation?.(value);
                            setOpenFormation(false);
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p>{formationLabel}</p>
              )}
            </div>
            {showMore && (
              <div className="mx-auto">
                <TacticFormation formation={formationId} />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex justify-between font-semibold">
              <p>{t("preset.buildUpStyle")}</p>
              {editable ? (
                <div className="relative flex flex-col">
                  <button
                    className="flex px-1 rounded bg-gray-775 desktop:hover:bg-gray-700 gap-x-0.5"
                    onClick={(e) => {
                      setOpenBuildUpStyle(!openBuildUpStyle);
                      setOpenFormation(false);
                      setOpenDefensiveApproach(false);
                      e.stopPropagation();
                    }}
                  >
                    <p className="font-medium">
                      {buildUpStyleLabel(buildUpStyle)}
                    </p>
                    <div
                      className={cn(
                        "pb-[2px] my-auto stroke-white",
                        openBuildUpStyle && "rotate-180",
                      )}
                    >
                      {dropdownArrow}
                    </div>
                  </button>
                  {openBuildUpStyle && (
                    <Dropdown
                      divRef={ref}
                      items={buildUpStyles}
                      label={buildUpStyleLabel}
                      onClick={(buildUpStyle) => {
                        setOpenBuildUpStyle(false);
                        onChangeBuildUpStyle?.(buildUpStyle);
                      }}
                    />
                  )}
                </div>
              ) : (
                <p>{buildUpStyleLabel(buildUpStyle)}</p>
              )}
            </div>
            {showMore && (
              <p className="text-gray-300">
                {buildUpStyleDescKey && <span>{t(buildUpStyleDescKey)}</span>}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex justify-between font-semibold">
              <p className=" whitespace-nowrap">
                {t("preset.defensiveApproach")}
              </p>

              {editable ? (
                <div className="relative flex flex-col">
                  <button
                    className="flex px-1 rounded bg-gray-775 desktop:hover:bg-gray-700 gap-x-0.5"
                    onClick={(e) => {
                      setOpenDefensiveApproach(!openDefensiveApproach);
                      setOpenFormation(false);
                      setOpenBuildUpStyle(false);
                      e.stopPropagation();
                    }}
                  >
                    <p className="font-medium">
                      {defensiveApproachLabel(defensiveApproach)}
                    </p>
                    <div
                      className={cn(
                        "pb-[2px] my-auto stroke-white",
                        openDefensiveApproach && "rotate-180",
                      )}
                    >
                      {dropdownArrow}
                    </div>
                  </button>
                  {openDefensiveApproach && (
                    <Dropdown
                      divRef={ref}
                      items={defApproaches}
                      label={defensiveApproachLabel}
                      onClick={(item) => {
                        setOpenDefensiveApproach(false);
                        onChangeDefensiveApproach?.(item);
                      }}
                    />
                  )}
                </div>
              ) : (
                defensiveApproachLabel(defensiveApproach)
              )}
            </div>
            {showMore && (
              <p className="text-gray-300">
                {defensiveApproachDescKey && (
                  <span>{t(defensiveApproachDescKey)}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex gap-x-2.5 whitespace-nowrap">
            <p className="font-semibold">{t("preset.lineHeight")}</p>
            <div className="w-full relative rounded-xl flex my-auto h-[7px] bg-white/20 ">
              <div
                className="absolute h-[7px] flex items-center bg-primary-blue rounded-xl"
                style={{
                  width: `calc(${
                    100 * (lineHeight > 100 ? 1 : lineHeight / 100)
                  }%)`,
                }}
              />
            </div>
            {editable ? (
              <Input
                value={lineHeight}
                onChange={(value) => onChangeLineHeight?.(value)}
              />
            ) : (
              <p className="font-semibold">{lineHeight}</p>
            )}
          </div>
          <button
            className="px-8 py-1 mx-auto text-xs rounded-md bg-white/10 desktop:hover:bg-white/20"
            onClick={() => setShowMore((prev) => !prev)}
          >
            {showMore ? t("common:showLess") : t("common:showMore")}
          </button>
        </div>
      )}
    </div>
  );
};

const Dropdown = ({ divRef, items, label, onClick }) => {
  return (
    <div
      ref={divRef}
      className="absolute p-0.5 rounded bg-gray-775 top-[18px] z-20 gap-y-1 right-0 shadow-dropdown"
    >
      {items.map((item) => (
        <button
          key={item}
          className="w-full text-xs font-medium text-right rounded-sm desktop:hover:bg-gray-700 px-0.5 whitespace-nowrap"
          onClick={() => onClick(item)}
        >
          {label(item)}
        </button>
      ))}
    </div>
  );
};

const Input = ({ value, onChange }) => {
  const [isFocused, setIsFocused] = useState(false);
  let min = 1;
  let max = 100;

  return (
    <input
      className={cn(
        "rounded-sm text-center text-white desktop:hover:placeholder:text-secondary-dark placeholder:text-xs text-xs placeholder:text-gray-600 outline-hidden border-[1px]  focus:border-primary-dark-blue caret-primary-dark-blue leading-3",
        !isFocused
          ? "bg-gray-775 border-black/0 desktop:hover:bg-gray-700 font-medium"
          : "border-primary-blue bg-black/30  desktop:hover:bg-black/75",
        "w-[35px]",
      )}
      type="number"
      min={min || 1}
      max={max || 99}
      onChange={(e) => {
        onChange(parseInt(e.target.value) || null);
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        max = max || 99;
        min = min || 1;
        if (parseInt(e.target.value) < min) onChange(min);
        else if (parseInt(e.target.value) > max) onChange(max);
        setIsFocused(false);
      }}
      value={value || ""}
      onKeyDown={(e) => e.key === "Escape" && e.currentTarget.blur()}
    />
  );
};
